// The notice that a prompt was adjusted (ai_app_backend #1508; Andy, 4 Nov 2026: "when it's rewritten we need to let
// the user know in a subtle notification that appears then disappears"). v2 may change a person's words as little as
// it can so they meet the content rules, then makes the picture or clip from the changed words. Its answer to placing
// the order (`PlacedOrder`: POST /v2/orders and POST /v2/character-portraits) then says `adjusted: true`, and
// `ladder_warning: true` when the words still count as a refused attempt. The adapters that place an order call
// `tellIfAdjusted` with that answer; the one toast mounted in the root layout (component/PromptAdjustedToast.tsx)
// listens and shows it for a few seconds. The toast never shows the changed words or the rule. Null (a repeat of the
// same receipt number: the first answer already said it) and false say nothing.
//
// The same event carries the changed words back to the person's boxes (Andy, 4 Nov 2026: "if the prompt has been
// changed, update it for them too. so they know how it changed, and arent confused … like the ai rewriter"). The
// answer's `adjusted_words` names each changed field as the order sent it (`prompt`, `negative_prompt`, an edit's
// `instruction`, a clip from words' `from.picture.prompt` …) with its new words; a field left out keeps what the person
// typed. The screen that owns a box (lib/use-adjusted-words.ts) puts the new words in only while the box still holds
// the words that were sent, so nothing typed since is lost and a box on another screen is never touched. The words
// never go to analytics.

import {
  REASON_KEYS,
  type RefusalReason,
  refusalReason,
} from './refusal-reason.ts'

/** One field v2 changed: its name as the order sent it, the words sent there, and the words it makes instead. */
export type AdjustedChange = { field: string; sent: string; adjusted: string }

/**
 * What the toast says (always that the prompt was adjusted, and the warning about repeated attempts when v2 asks),
 * and for the boxes: the order's kind (`picture`, `clip` …) and the fields whose words changed.
 */
export type AdjustedNotice = {
  ladderWarning: boolean
  kind: string
  changes: AdjustedChange[]
  /** A suggestion's rule category (lib/refusal-reason.ts), when the refusal names one; never on an adjusted order. */
  reason?: RefusalReason
}

/** The window event the toast and the boxes listen for, its `detail` an `AdjustedNotice`. */
export const ADJUSTED_EVENT = 'ai-app:prompt-adjusted'

/** The translation keys of the toast's two sentences (`en` holds the English). */
export const ADJUSTED_KEY = 'promptAdjustedNotice'
export const LADDER_WARNING_KEY = 'promptAdjustedLadderWarning'

/** The value at a dotted field name (`from.picture.prompt`) of the order sent, or undefined. */
function valueAt(order: unknown, field: string): unknown {
  let value = order
  for (const part of field.split('.')) {
    if (typeof value !== 'object' || value === null) return undefined
    value = (value as Record<string, unknown>)[part]
  }
  return value
}

/** Each field of `words` (an answer's `adjusted_words` or a refusal's `suggested_words`) the order sent words at. */
function changesOf(words: unknown, sent: unknown): AdjustedChange[] {
  const changes: AdjustedChange[] = []
  if (typeof words === 'object' && words !== null)
    for (const [field, adjusted] of Object.entries(words)) {
      const before = valueAt(sent, field)
      if (typeof adjusted === 'string' && typeof before === 'string')
        changes.push({ field, sent: before, adjusted })
    }
  return changes
}

/**
 * The notice an order's placing answer asks for, or null when it asks for none (only `adjusted: true` does). `sent`
 * is the order as placed: a changed field counts only where the order sent words of its own.
 */
export function adjustedNotice(
  placed: unknown,
  sent?: unknown,
): AdjustedNotice | null {
  if (typeof placed !== 'object' || placed === null) return null
  const { adjusted, ladder_warning, kind, adjusted_words } = placed as {
    adjusted?: unknown
    ladder_warning?: unknown
    kind?: unknown
    adjusted_words?: unknown
  }
  if (adjusted !== true) return null
  return {
    ladderWarning: ladder_warning === true,
    kind: typeof kind === 'string' ? kind : '',
    changes: changesOf(adjusted_words, sent),
  }
}

/** The translation keys the toast shows for `notice`, in order. */
export const adjustedNoticeKeys = (notice: AdjustedNotice): string[] =>
  notice.ladderWarning ? [ADJUSTED_KEY, LADDER_WARNING_KEY] : [ADJUSTED_KEY]

/**
 * A word box on the screen: the order kinds it was sent for, the fields it was sent at (one box may be sent at
 * several: the video prompt is both a clip from words' motion and its picture's prompt; the first changed field
 * named decides), and what it holds now.
 */
export type WordsBox = {
  kinds: readonly string[]
  fields: readonly string[]
  value: string
}

/**
 * The words `box` holds once it follows `notice`, or null when it doesn't follow it: only for an order of the box's
 * kind, a field of the box's that changed, and while the box still holds the words sent (spaces at the ends aside, as
 * the adapters trim some) or already the words made, so words typed since, or a box the order didn't come from, are
 * left as they are.
 */
export function takesWords(
  notice: AdjustedNotice,
  box: WordsBox,
): string | null {
  if (!box.kinds.includes(notice.kind)) return null
  for (const field of box.fields) {
    const change = notice.changes.find((each) => each.field === field)
    if (!change) continue
    if (box.value === change.adjusted) return change.adjusted
    return box.value.trim() === change.sent.trim() ? change.adjusted : null
  }
  return null
}

/** The words `box` should be set to now that v2 adjusted them, or null to leave it (`takesWords`, when not already). */
export function wordsForBox(
  notice: AdjustedNotice,
  box: WordsBox,
): string | null {
  const words = takesWords(notice, box)
  return words === null || words === box.value ? null : words
}

/**
 * Tells the toast, and the boxes, when an order's placing answer says its words were adjusted; does nothing otherwise.
 * `sent` is the order as placed, for the boxes; without it only the toast hears.
 */
export function tellIfAdjusted(placed: unknown, sent?: unknown): void {
  const notice = adjustedNotice(placed, sent)
  if (!notice || typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(ADJUSTED_EVENT, { detail: notice }))
}

// With v2's `moderation_fixer_mode` `suggest` (Andy, 4 Nov 2026: "instead of the rewrite being passed onto the AI, it
// just returns the prompt to users frontend. Like sorry your prompt couldn't be used, I've adjusted it to meet our
// compliance standards, send it if you want"), words the judge refuses but the rewriter could fix are not made:
// `POST /v2/orders` answers `422 words_refused`, nothing charged, with `suggested_words` (the same keys and kind of
// words as `adjusted_words`). The adapter tells the screen's boxes by a second event, which the toast never hears;
// each box still holding the words sent takes the suggestion, and the screen then offers to send it as the person's
// own new order (component/WordsSuggestedModal.tsx). No `suggested_words` (or none for a field the order sent) is an
// ordinary refusal, shown as before.

/** The window event the boxes listen for when v2 refused the words with a suggestion, its `detail` an `AdjustedNotice`. */
export const SUGGESTED_EVENT = 'ai-app:prompt-suggested'

/** The translation keys of the suggestion's heading, its line about the adjusted words, and its two buttons (`en`
 * holds the English). The rule category the refusal names is said between the heading and that line (Andy, 4 Nov
 * 2026: "mention the rule category it violated though in the rewrite sendback"). */
export const SUGGESTED_REFUSED_KEY = 'promptSuggestedRefused'
export const SUGGESTED_ADJUSTED_KEY = 'promptSuggestedAdjusted'
export const SEND_SUGGESTED_KEY = 'promptSuggestedSend'
export const EDIT_SUGGESTED_KEY = 'promptSuggestedEdit'

/**
 * The fields of v2's `422 words_refused` read here. The contract types error bodies as `ErrorBody` with any extra
 * field, so this names them until the contract regenerates from the backend's suggest change (backend #1508):
 * `suggested_words` keyed as `PlacedOrder.adjusted_words` (the prompt or instruction always, the negative prompt only
 * when changed), absent or null when v2 suggests nothing.
 */
export type SuggestedRefusal = {
  code?: string
  ladder_warning?: boolean | null
  suggested_words?: { [field: string]: string } | null
}

/**
 * The suggestion a refusal of the order `sent` carries, as a notice for the boxes, or null for an ordinary refusal:
 * only a `words_refused` with `suggested_words` naming a field the order sent words at. The kind is the order's.
 */
export function suggestedNotice(
  refused: unknown,
  sent: unknown,
): AdjustedNotice | null {
  if (typeof refused !== 'object' || refused === null) return null
  const { code, suggested_words, ladder_warning } = refused as {
    [K in keyof SuggestedRefusal]?: unknown
  }
  if (code !== 'words_refused') return null
  const changes = changesOf(suggested_words, sent)
  if (changes.length === 0) return null
  const kind = valueAt(sent, 'kind')
  const reason = refusalReason(refused)
  return {
    ladderWarning: ladder_warning === true,
    kind: typeof kind === 'string' ? kind : '',
    changes,
    ...(reason ? { reason } : {}),
  }
}

/**
 * The translation keys of each part of the suggestion's message for `notice` (Andy, 4 Nov 2026: "make it look more
 * well organized and easier on eyes"): the heading, that the prompt couldn't be used; the rule category's line when
 * the refusal names one, else null; that it was adjusted; and the warning about repeated attempts when v2 asks, else
 * null.
 */
export type SuggestedParts = {
  heading: string
  reason: string | null
  adjusted: string
  warning: string | null
}

export const suggestedParts = (notice: AdjustedNotice): SuggestedParts => ({
  heading: SUGGESTED_REFUSED_KEY,
  reason: notice.reason ? REASON_KEYS[notice.reason] : null,
  adjusted: SUGGESTED_ADJUSTED_KEY,
  warning: notice.ladderWarning ? LADDER_WARNING_KEY : null,
})

/** Tells the boxes when v2 refused the order `sent` with words it would make instead; does nothing otherwise. */
export function tellIfSuggested(refused: unknown, sent: unknown): void {
  const notice = suggestedNotice(refused, sent)
  if (!notice || typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(SUGGESTED_EVENT, { detail: notice }))
}
